import {
  mdiRocketLaunchOutline,
  mdiCartPlus,
  mdiWeb,
  mdiClockOutline,
  mdiPlayBoxOutline,
  mdiChartLine,
  mdiBellOutline,
  mdiTuneVertical,
  mdiDiscord,
  mdiAccountCircle,
  mdiLifebuoy,
  mdiWrenchOutline,
} from "@mdi/js";

/*
 * The /tutorial page's content.
 *
 * Text is static and trusted, and rendered with v-html so it can carry <b>
 * and links. `[3]` becomes a numbered disc matching marker 3 drawn on that
 * step's screenshot.
 *
 * Screenshots live in public/tutorial and are generated, arrows included, by
 * e2e/tutorial/*.shots.mjs (`npx playwright test -c
 * playwright.tutorial.config.mjs` in e2e/). When a screen changes, re-run that
 * rather than editing images by hand, and keep the [n] references here in step
 * with the markers there.
 *
 * `mobile: true` — a phone version exists as public/tutorial/<image>-m.webp
 * (e2e/tutorial/*.mobile.shots.mjs), with the same [n] markers.
 * `video: "<name>"` — a short clip exists as public/tutorial/<name>.webm/.mp4
 * with <name>-poster.webp (e2e/tutorial/flows.video.shots.mjs).
 */
export const quickStart = [
  { label: "Buy tokens", section: "tokens" },
  { label: "Pick a toplist", section: "order" },
  { label: "Set link, votes & speed", section: "order" },
  { label: "Pay from your balance", section: "order" },
  { label: "Watch it climb", section: "manage" },
];

export const sections = [
  {
    id: "start",
    title: "The panel at a glance",
    icon: mdiRocketLaunchOutline,
    intro:
      "Toplistbot sends votes to your server's page on toplist websites. You pay for votes with <b>tokens</b>, and every order you place becomes a <b>campaign</b> that delivers its votes over time, at the speed you choose.",
    steps: [
      {
        title: "Your dashboard",
        image: "dashboard",
        alt: "The dashboard with the token balance, Buy tokens and Start campaign buttons and the vote totals highlighted",
        body: [
          "Your token balance is always in the top bar [1]. When it runs low, <b>Buy tokens</b> [2] tops it up.",
          "<b>Start campaign</b> [3] takes you to the list of toplist sites, where every order begins.",
          "The tiles [4] add up what all your campaigns have delivered, what is running now and how many votes are still to go.",
        ],
        link: { to: "/dashboard", label: "Open the dashboard" },
      },
    ],
    tips: [
      "Verify your e-mail address and you get <b>1 free token</b> to try things out.",
      "Press <b>Ctrl + K</b> anywhere to jump straight to any page.",
      "Stuck on a page? <b>Guide</b> at the bottom of the menu (or the <b>?</b> in the top bar on wide screens) opens this tutorial at the step for that page.",
    ],
  },
  {
    id: "tokens",
    title: "Buy tokens",
    icon: mdiCartPlus,
    intro:
      "Every vote is paid for in tokens, so top up first. Tokens never expire, and they are only spent when you place an order.",
    steps: [
      {
        title: "Choose a package",
        image: "buy-tokens",
        mobile: true,
        alt: "The Buy tokens page with the package tabs and a Buy button highlighted",
        body: [
          "Open <b>Billing → Buy tokens</b>. <b>Token packages</b> [1] are one-off purchases. Bigger packages cost less per token.",
          "<b>Subscriptions</b> [2] top your balance up every month with 10% extra tokens. You can cancel any time.",
          "Click <b>Buy</b> [3] next to the package you want. The screenshot shows example prices; the page always shows the current ones.",
        ],
        link: { to: "/products", label: "Go to Buy tokens" },
      },
      {
        title: "Pay by card or crypto",
        image: "payment-method",
        alt: "The payment method page with card and crypto options and the Proceed to payment button",
        body: [
          "Pick <b>Card &amp; more</b> [1] for a secure Stripe checkout, or <b>Crypto</b> [2] for Bitcoin, USDT, ETH and more.",
          "Click <b>Proceed to payment</b> [3] and finish on the provider's page. Nothing is charged before that.",
          "Card payments arrive in your balance straight away. Crypto arrives once the network confirms the payment.",
        ],
      },
    ],
  },
  {
    id: "order",
    title: "Order votes",
    icon: mdiWeb,
    intro:
      "An order is votes for one server on one toplist. You can put several orders in your cart and pay for them all at once.",
    steps: [
      {
        title: "Find your toplist",
        image: "websites-list",
        mobile: true,
        alt: "The Websites page with the search box, an Order button and the voting plan button highlighted",
        body: [
          "Open <b>Websites</b> [1] in the menu. It lists every toplist we can vote on, with the price per 100 votes and the top speed.",
          "Type the site's name in the search box [2], or filter by game with the buttons underneath it.",
          "Click <b>Order</b> [3] on your site. The clock button [4] starts a voting plan instead (see the next section).",
          "Sites that are temporarily unavailable are hidden. Switch on <b>Show unavailable</b> to see them and why.",
        ],
        link: { to: "/websites", label: "Browse websites" },
      },
      {
        title: "Fill in the order",
        image: "order-dialog",
        mobile: true,
        alt: "The order form with the voting link, votes, speed and Add to cart button highlighted",
        body: [
          "<b>Voting link</b> [1]: paste the page where the vote button or captcha is for <i>your</i> server. It has to be on that toplist's own website.",
          "<b>Votes</b> [2]: how many votes you want, at least 15. <b>Max</b> fills in as many as your balance covers.",
          "<b>Speed</b> [3]: votes per hour. Slower looks more natural; the site's maximum is shown next to it.",
          "Some sites also ask for a <b>Vote target</b>, such as your character or server name. It is case sensitive.",
          "Check the <b>Total</b>, then click <b>Add to cart</b> [4].",
        ],
      },
      {
        title: "Check out",
        image: "checkout",
        mobile: true,
        video: "order-flow",
        alt: "The cart with the votes field, the remove button and Complete purchase highlighted",
        body: [
          "Your cart lists every order. You can still change the votes or speed [1], or remove a line [2].",
          "The <b>Order summary</b> shows the total and what your balance will be afterwards. If you are short, it links you to <b>Buy tokens</b> with the exact amount.",
          "Click <b>Complete purchase</b> [3]. The tokens come off your balance; no card is charged here. Your campaigns start within a few minutes and you land on <b>Campaigns</b>.",
        ],
        link: { to: "/checkout", label: "Open your cart" },
      },
    ],
  },
  {
    id: "plans",
    title: "Voting plans",
    icon: mdiClockOutline,
    intro:
      "A voting plan votes around the clock for a fixed number of days, instead of delivering a set number of votes. It is the easiest way to hold a ranking. Sites that offer one carry a <b>Voting plan</b> badge.",
    steps: [
      {
        title: "Set up a plan",
        image: "voting-plan",
        alt: "The voting plan page with plan lengths, the voting URL, the agreement checkbox and Add to cart highlighted",
        body: [
          "Pick the plan length [1]: <b>Daily</b>, <b>Weekly</b> or <b>Monthly</b>. Longer plans are cheaper per day.",
          "Paste your <b>Voting URL</b> [2]. This one cannot be changed later, so double-check it.",
          "Tick the box [3] to confirm you understand a plan buys vote <i>attempts</i>: some votes may not count because of the website's own rules.",
          "Click <b>Add to cart</b> [4] and check out as usual.",
        ],
      },
    ],
  },
  {
    id: "manage",
    title: "Manage your campaigns",
    icon: mdiPlayBoxOutline,
    intro:
      "Every order becomes a campaign. <b>Campaigns</b> in the menu shows them all with live progress, and refreshes by itself.",
    steps: [
      {
        title: "The campaign list",
        image: "campaigns-list",
        mobile: true,
        alt: "The campaigns list with the status tabs, the progress column and the four action buttons highlighted",
        body: [
          "The tabs [1] split your campaigns into <b>Running</b>, <b>Paused</b>, <b>Completed</b> and <b>Hidden</b>. The search box finds a campaign by tag, website, link or ID.",
          "<b>Progress</b> [2] shows how many of the ordered votes have been delivered. <b>Speed</b> and <b>Finish in</b> sit next to it.",
          "Each row has four buttons: pause or resume [3], quick adjust [4], edit [5] and more [6]. They are explained below.",
          "Click the pencil next to a website's name to give a campaign <b>tags</b>, for example the server it belongs to. Tagged campaigns get their own tab.",
        ],
        link: { to: "/campaigns", label: "Open Campaigns" },
      },
      {
        title: "Add votes or change the speed",
        image: "campaigns-quick-adjust",
        mobile: true,
        video: "quick-adjust-flow",
        alt: "The quick adjust panel with the votes field, the speed field and the confirm button highlighted",
        body: [
          "The <b>+</b> button opens <b>Quick adjust</b>. Use the buttons or type a new total [1] to add or remove votes.",
          "Change the delivery speed [2] here too.",
          "The confirm button [3] says exactly what will happen, such as <i>Add 500 votes · 1.00 tokens</i>. Removing votes refunds the tokens for them.",
        ],
      },
      {
        title: "Pause and resume",
        image: "campaigns-pause",
        alt: "The pause confirmation dialog with Pause campaign highlighted",
        body: [
          "The pause button stops voting until you resume it. Nothing is lost: the votes you paid for stay on the campaign [1].",
          "Find paused campaigns in the <b>Paused</b> tab and press the same button to resume them.",
          "To act on several at once, tick their checkboxes and use the bar that appears.",
        ],
      },
      {
        title: "Edit a campaign",
        image: "campaigns-edit",
        alt: "The edit dialog with the voting link and the Next button highlighted",
        body: [
          "The wrench opens the editor. Step 1 holds the <b>Voting link</b> [1] and an optional <b>Referral link</b>. Some sites require a referral link; those campaigns show a <b>Needs referral</b> chip until it is set.",
          "<b>Next</b> [2] leads to the vote settings: your <b>Voting profile</b> and <b>Proxy profile</b> (see Optional settings), the total votes and the speed.",
          "The last step shows a summary of the changes, and any cost or refund, before you apply them.",
          "Something wrong with a campaign? The <b>Report</b> button at the top starts a support chat about it.",
        ],
      },
      {
        title: "More actions",
        image: "campaigns-more-menu",
        alt: "The More menu with Daily limit, View logs and Hide highlighted",
        body: [
          "<b>Daily limit</b> [1] caps how many votes a campaign may get per day.",
          "<b>View logs</b> [2] opens every vote this campaign delivered (next section).",
          "<b>Hide</b> [3] tidies a campaign out of your tabs. It is not deleted: you can bring it back from the <b>Hidden</b> tab.",
        ],
      },
    ],
  },
  {
    id: "results",
    title: "Logs & results",
    icon: mdiChartLine,
    intro: "Every campaign keeps a log of its votes from the last 15 days. Open it from <b>More → View logs</b>.",
    steps: [
      {
        title: "Votes per hour",
        image: "logs-chart",
        alt: "The campaign performance chart with the votes in the last 24 hours highlighted",
        body: [
          "The <b>Performance</b> chart shows the votes per hour over the last 24 hours, with the total [1] and the busiest hour.",
          "<b>Campaigns → Campaign Chart</b> in the menu shows the same chart with a picker to switch between campaigns.",
        ],
      },
      {
        title: "Every single vote",
        image: "logs-table",
        alt: "The vote log table with the search box and a status label highlighted",
        body: [
          "<b>All Votes Data</b> lists each vote with its time, IP address, browser and username. Search it [1] or filter by status and time.",
          "Each vote is <b>Complete</b> or <b>Partial</b> [2]. Partial means the IP address or username was not recorded.",
          "Above it, <b>Grouped by username</b> shows how often each name voted.",
        ],
      },
    ],
    tips: [
      "Votes not showing on the toplist? Check the logs first, then the voting link. Most problems are a wrong link or a missing referral link. Send us the campaign ID and we can look at the same logs.",
    ],
  },
  {
    id: "notifications",
    title: "Notifications",
    icon: mdiBellOutline,
    intro: "When something needs your attention, such as a toplist that changed its vote page, we tell you here.",
    steps: [
      {
        title: "The bell",
        image: "notifications",
        alt: "The notifications panel open under the bell with View all notifications highlighted",
        body: [
          "The bell [1] in the top bar shows a red count for unread notices. Click it for the latest ones.",
          "A notice can carry a button straight to the campaign it is about, and <b>Discuss this</b> opens a chat with us about it.",
          "<b>View all notifications</b> [2] opens the full history.",
        ],
        link: { to: "/notifications", label: "All notifications" },
      },
    ],
  },
  {
    id: "settings",
    title: "Optional settings",
    icon: mdiTuneVertical,
    intro:
      "You do not need any of these to get started: every campaign has sensible defaults. They let you fine-tune how your votes look.",
    steps: [
      {
        title: "Vote profiles: which usernames vote",
        image: "vote-profile",
        alt: "The create voting profile dialog with the name, usernames and Create profile button highlighted",
        body: [
          "A vote profile is a pool of usernames. Every vote picks a random name from it.",
          "Open <b>Settings → Vote Profiles</b> and click <b>New voting profile</b>. Give it a name [1] and enter one username per line [2]. 5 to 20 realistic names works best.",
          "Save it [3], then pick it under <b>Voting Profile</b> when you edit a campaign.",
        ],
        link: { to: "/advanced/profiles", label: "Vote profiles" },
      },
      {
        title: "Proxy profiles: where votes come from",
        image: "proxy-profile",
        alt: "The create proxy profile dialog with the quick presets and Create profile button highlighted",
        body: [
          "A proxy profile picks the countries your votes appear to come from. Without one, votes come from a mix of Europe and the US.",
          "Open <b>Settings → Proxy Profiles</b>, click <b>New proxy profile</b>, name it and choose countries. The quick presets [1] fill in a sensible region. 3 to 8 countries works best.",
          "Save it [2], then pick it under <b>Proxy Profile</b> when you edit a campaign.",
        ],
        link: { to: "/advanced/profiles/proxy", label: "Proxy profiles" },
      },
      {
        title: "Discord tokens (Discadia only)",
        image: "discord-tokens",
        alt: "The upload Discord tokens dialog with the token box and Upload button highlighted",
        body: [
          "Only campaigns on <b>Discadia</b> need Discord tokens. All other sites work without them.",
          "Each account must already be in the target Discord server, be at least 30 days old and have a verified e-mail address.",
          "Open <b>Discord Tokens</b>, click <b>Bulk upload</b>, paste one token per line [1] and click <b>Upload</b> [2]. Every campaign uses them automatically.",
        ],
        link: { to: "/discord-tokens", label: "Discord tokens" },
      },
    ],
  },
  {
    id: "account",
    title: "Account & invoices",
    icon: mdiAccountCircle,
    steps: [
      {
        title: "API key and security",
        image: "account-security",
        alt: "The account Security tab with the API key copy button highlighted",
        body: [
          "<b>Settings → Account</b> has four tabs. On <b>Security</b> [1] you can copy your <b>API key</b> [2] to run campaigns from your own code. Treat it like a password.",
          "The same tab turns on <b>two-factor authentication</b> and sends you a password reset link.",
          "<b>Notifications</b> sets your e-mails, vote pop-ups and a webhook. <b>Appearance</b> lets you pick the panel's colour.",
        ],
        link: { to: "/account", label: "Your account" },
      },
      {
        title: "Invoices",
        image: "invoices",
        alt: "The invoices list with the payment details button and Export CSV highlighted",
        body: [
          "<b>Billing → Invoices</b> lists every token purchase with its amount, status and date.",
          "The eye button [1] opens the payment details, with the invoice PDF for card payments. <b>Export CSV</b> [2] downloads the whole list.",
        ],
        link: { to: "/invoices", label: "Invoices" },
      },
    ],
  },
  {
    id: "troubleshooting",
    title: "Troubleshooting",
    icon: mdiWrenchOutline,
    intro: "Most problems have a fix you can make yourself in the panel. Start here before you write to us.",
    steps: [
      {
        title: "Votes aren't showing on the toplist",
        image: "trouble-votes",
        alt: "The campaign editor with the voting link, the referral link and the Report button highlighted",
        body: [
          "Open the campaign's logs first (<b>More → View logs</b>). Every vote we delivered in the last 15 days is listed there, as <b>Complete</b> or <b>Partial</b>.",
          "Then open the editor with the wrench and check the <b>Voting link</b> [1]. It must be the page with the vote button or captcha for <i>your</i> server, on that toplist's own website. The order form rejects a link on another website, but it cannot tell whether the page is yours.",
          "Some sites only count a vote that comes with a <b>Referral link</b> [2]. See <b>Needs referral</b> below.",
          "Hover <b>Progress</b> in the campaign list to see how many attempts have been made. On some sites not every attempt counts, so a campaign makes more attempts than the votes you ordered.",
          "Still not right? Click <b>Report</b> [3] at the top of the editor, or start a live chat, and give us the campaign ID.",
        ],
        link: { to: "/campaigns", label: "Open Campaigns" },
      },
      {
        title: "Needs referral",
        image: "trouble-referral",
        mobile: true,
        alt: "The Campaigns page with the needs a referral link banner and a Needs referral chip highlighted",
        body: [
          "Some toplists only count a vote when it looks like the visitor came from another page. That page is the <b>referral link</b>.",
          "A campaign on such a site without one shows a <b>Needs referral</b> chip [2], and a banner at the top of <b>Campaigns</b> lists every campaign that needs one [1]. Until you add it, votes are spent without being counted.",
          "Click the chip or the campaign's button in the banner. The editor opens at the <b>Referral Link</b> field. Use your own website or community page, or click the suggested link, then save.",
        ],
        link: { to: "/campaigns", label: "Open Campaigns" },
      },
      {
        title: "Not enough tokens",
        image: "trouble-tokens",
        mobile: true,
        alt: "The cart with the You need more tokens notice and its Buy tokens link highlighted",
        body: [
          "In the order form, <b>Add to cart</b> is switched off when your balance doesn't cover the 15-vote minimum on that site. The note above the button says so and links to <b>Buy tokens</b>.",
          "In the cart, the <b>Order summary</b> shows <b>You need … more tokens</b> [1] when the total is more than your balance, and <b>Complete purchase</b> stays off.",
          "<b>Buy tokens</b> [2] there takes the missing amount with it to the packages page. Or lower the votes in your cart until it fits.",
        ],
        link: { to: "/products", label: "Go to Buy tokens" },
      },
      {
        title: "A site is unavailable",
        image: "trouble-unavailable",
        mobile: true,
        alt: "The Websites page with Show unavailable switched on and the Broken, Being fixed and Unavailable labels highlighted",
        body: [
          "<b>Websites</b> hides sites that can't be ordered right now. Switch on <b>Show unavailable</b> [1] to see them; the number next to it is how many are hidden.",
          "<b>Broken</b> [2] means the site is not working and orders will not deliver. <b>Being fixed</b> [3] means it is misbehaving and under repair. Both show <b>Unavailable</b> [4] instead of an Order button.",
          "Two other labels can still be ordered: <b>Awaiting activation</b> (orders wait until the site goes live) and <b>Manual task</b> (handled by hand, so slower).",
          "If a site stops working while you have a campaign on it, the campaign shows a <b>Site offline</b> chip. We are working on it; contact support for the status.",
        ],
        link: { to: "/websites", label: "Browse websites" },
      },
      {
        title: "Refunds and reporting a problem",
        image: "trouble-report",
        alt: "The Report Issue With Campaign form with the issue, the preferred solution, the votes to return and Continue to chat highlighted",
        body: [
          "Open the campaign editor with the wrench and click <b>Report</b>. Pick what you are seeing [1]: <b>Lost Votes</b>, <b>Slow Voting Speed</b>, <b>Deleted From Toplist</b> or <b>Other Issue</b>.",
          "Choose the solution you would prefer [2]: a refund of some votes, of all votes, or something else. For a refund, say how many votes should be returned [3]. An estimate is fine.",
          "<b>Continue to chat</b> [4] opens a live chat with your report already written out, campaign ID included. Add anything else and send it.",
          "Only want fewer votes? Removing votes in <b>Quick adjust</b> refunds the tokens for them.",
        ],
      },
    ],
    tips: [
      "Never use toplistbot.com as a referral link: the panel refuses it.",
      "Put the campaign ID (such as #1041) in any message to us. It is on every campaign and at the top of the editor.",
    ],
  },
  {
    id: "help",
    title: "Get help",
    icon: mdiLifebuoy,
    steps: [
      {
        title: "Talk to us",
        image: "support",
        alt: "The support page with Start live chat and Join Discord highlighted",
        body: [
          "<b>Start live chat</b> [1] reaches a person on weekdays; the page shows whether we are open right now. Outside those hours we reply by e-mail.",
          "Or ask on our <b>Discord</b> server [2]. The <b>Help</b> button at the bottom of the menu opens the chat from any page.",
          "Mention the campaign ID if your question is about one: it saves a round trip.",
        ],
        link: { to: "/support", label: "Support" },
      },
    ],
  },
];
